import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Button,
  Card,
  CardHeader,
  CardSection,
  Gutter,
  IconButton,
  Layout,
  LayoutItem,
  SmallHeader,
  ReadonlyTextarea,
  useApi,
  Banner,
  Grid,
  GridRow,
  GridColumn,
  SectionHeader,
  Flashy,
  Paragraph,
  CondensedSmall,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';
import { ProfileCompletionConst } from '@axiom/const';
import { CandidateProfileIssuesUtils } from '@axiom/utils';

import { CandidateApi } from '../../api/candidate-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

import { ProfileSummaryEdit } from './ProfileSummaryEdit';

export const ProfileSummary = ({
  candidateId,
}: {
  candidateId: Candidate['id'];
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [{ data: candidate }, { data: profileIssues }] = useApi(
    CandidateApi.readCandidate(candidateId),
    CandidateProfileIssuesApi.readCandidateProfileIssues(candidateId)
  );

  const candidateSummaryLengthEmpty =
    CandidateProfileIssuesUtils.hasSummaryOfAnyLengthIssues(
      profileIssues.issues
    );
  const candidateSummaryLengthTooShort =
    CandidateProfileIssuesUtils.hasSummaryOfSufficientLengthIssues(
      profileIssues.issues
    );

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleModalClose = () => {
    setIsEditMode(false);
  };

  useEffect(() => {
    if (searchParams.get('modal') === 'profileSummary') {
      handleEditClick();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  return (
    <>
      <Card name="PROFILE_SUMMARY">
        <CardHeader>
          <Layout position="middle">
            <LayoutItem fluid>
              <SmallHeader name="PENDO-PROFILE-SUMMARY">Summary</SmallHeader>
            </LayoutItem>
            {!isEditMode && (
              <IconButton
                icon="pencil"
                name="EDIT_PROFILE_SUMMARY_BUTTON"
                onClick={handleEditClick}
                pattern="secondary"
                variation="minimal"
              />
            )}
          </Layout>
        </CardHeader>
        <CardSection divider>
          {!!(
            candidateSummaryLengthEmpty || candidateSummaryLengthTooShort
          ) && (
            <Gutter bottom="16px">
              <Banner name="PROFILE_SUMMARY_BANNER" type="info" impact="high">
                <Grid>
                  <GridRow>
                    <GridColumn smallScreenWidth={12} largeScreenWidth={9}>
                      <Layout horizontalGutter="8px" position="middle" wrap>
                        <LayoutItem>
                          <SectionHeader name="PROFILE_SUMMARY_BANNER_TITLE">
                            {!!candidateSummaryLengthEmpty &&
                              ProfileCompletionConst
                                .ProfileCompletionSummaryBanner.emptyTitle}
                            {!!candidateSummaryLengthTooShort &&
                              ProfileCompletionConst
                                .ProfileCompletionSummaryBanner.tooShortTitle}
                          </SectionHeader>
                        </LayoutItem>
                        <LayoutItem>
                          <CondensedSmall>
                            <Flashy color="textSubtle">|</Flashy>
                          </CondensedSmall>
                        </LayoutItem>
                        <LayoutItem>
                          <CondensedSmall>
                            <Flashy color="textSubtle">10 mins</Flashy>
                          </CondensedSmall>
                        </LayoutItem>
                      </Layout>
                      <Gutter bottom="8px" />
                      <Paragraph name="PROFILE_SUMMARY_BANNER_CONTENT">
                        {!!candidateSummaryLengthEmpty &&
                          ProfileCompletionConst.ProfileCompletionSummaryBanner
                            .emptyContent}
                        {!!candidateSummaryLengthTooShort &&
                          ProfileCompletionConst.ProfileCompletionSummaryBanner
                            .tooShortContent}
                      </Paragraph>
                      <Gutter bottom="16px" only="smallScreen" />
                    </GridColumn>
                    <GridColumn smallScreenWidth={12} largeScreenWidth={3}>
                      <Layout position="right">
                        <LayoutItem>
                          <Button
                            pattern="primary"
                            name="EDIT_PROFILE_SUMMARY_BANNER_BUTTON"
                            onClick={handleEditClick}
                          >
                            Edit Now
                          </Button>
                        </LayoutItem>
                      </Layout>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Banner>
            </Gutter>
          )}
          {!!candidate.candidateSummary && (
            <Gutter bottom="16px">
              <Layout>
                <LayoutItem fluid>
                  <ReadonlyTextarea
                    name="candidateSummaryReadonly"
                    value={candidate.candidateSummary}
                  />
                </LayoutItem>
              </Layout>
            </Gutter>
          )}
        </CardSection>
      </Card>
      {!!isEditMode && (
        <ProfileSummaryEdit
          name="PROFILE_SUMMARY_EDIT_FORM"
          candidate={candidate}
          issues={profileIssues.issues}
          onClose={() => {
            handleModalClose();
          }}
        />
      )}
    </>
  );
};
